import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://allchinabuyspreadsheet.icu";

export default function ArticlePage() {
  const { t } = useTranslation();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How to Use AllChinaBuy Spreadsheet 2026",
    "description": "A Complete Beginner's Guide to Finding Products, Trusted Sellers, and Getting the Best Deals on AllChinaBuy Spreadsheet",
    "author": {
      "@type": "Organization",
      "name": "AllChinaBuy Spreadsheet",
    },
    "datePublished": "2026-06-20",
    "dateModified": "2026-06-25",
    "publisher": {
      "@type": "Organization",
      "name": "AllChinaBuy Spreadsheet",
      "url": siteUrl,
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/allchinabuy-spreadsheet-guide`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Buying Guide",
        "item": `${siteUrl}/allchinabuy-spreadsheet-guide`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}></script>
      <Navbar />

      <main>
        <article className="pt-24 md:pt-28 pb-16">
          <div className="container-custom max-w-3xl mx-auto">
            <header className="text-center mb-10 md:mb-14">
              <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-300 text-xs font-medium text-foreground-600 mb-4">
                {t("article:tag", "Buying Guide")}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight mb-4">
                {t("article:title", "How to Use AllChinaBuy Spreadsheet 2026")}
              </h1>
              <p className="text-base md:text-lg text-foreground-500 font-light max-w-2xl mx-auto mb-6">
                {t("article:subtitle", "A Complete Beginner's Guide to Finding Products, Trusted Sellers, and Getting the Best Deals")}
              </p>
              <div className="flex items-center justify-center gap-3 text-xs text-foreground-500">
                <span>{t("article:authorName", "AllChinaBuy Team")}</span>
                <span className="text-foreground-300">·</span>
                <span>{t("article:authorSince", "Updated June 2026")}</span>
              </div>
            </header>

            <div className="prose-custom space-y-8 md:space-y-10">
              {/* Introduction */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {t("article:introTitle", "Getting Started with AllChinaBuy Spreadsheet")}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:introP1", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t("article:introP2", "")}
                  {" "}
                  <Link to="/" className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2">
                    explore the homepage
                  </Link>
                  {" "}to get started with the complete directory.
                </p>
              </section>

              <hr className="border-background-200" />

              {/* What Is an AllChinaBuy Spreadsheet */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {t("article:whatIsTitle", "What Is an AllChinaBuy Spreadsheet?")}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:whatIsP1", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:whatIsP2", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t("article:whatIsP3", "")}
                </p>
              </section>

              <hr className="border-background-200" />

              {/* Why Use */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {t("article:whyUseTitle", "Why Use an AllChinaBuy Spreadsheet in 2026?")}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:whyUseP1", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:whyUseP2", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t("article:whyUseP3", "")}
                  {" "}Browse the{" "}
                  <Link to="/" className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2">
                    main directory
                  </Link>
                  {" "}to see how organized and accessible the spreadsheet structure is.
                </p>
              </section>

              <hr className="border-background-200" />

              {/* How to Navigate */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {t("article:howToUseTitle", "How to Navigate the AllChinaBuy Spreadsheet")}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:howToUseP1", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:howToUseP2", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t("article:howToUseP3", "")}
                </p>
              </section>

              <hr className="border-background-200" />

              {/* Safety Tips */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {t("article:tipsTitle", "Essential Tips for Safe Buying")}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:tipsP1", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:tipsP2", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t("article:tipsP3", "")}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t("article:tipsP4", "")}
                </p>
              </section>

              <hr className="border-background-200" />

              {/* Comparison */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {t("article:comparisonTitle", "Spreadsheet vs. Direct Browsing: A Comparison")}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  {t("article:comparisonP1", "")}
                </p>

                <div className="overflow-x-auto rounded-2xl border border-background-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 md:px-6 py-3 font-semibold text-foreground-800">Feature</th>
                        <th className="text-left px-4 md:px-6 py-3 font-semibold text-foreground-800">Spreadsheet Directory</th>
                        <th className="text-left px-4 md:px-6 py-3 font-semibold text-foreground-800">Direct Browsing</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-background-200">
                        <td className="px-4 md:px-6 py-3 text-foreground-700">Time to Find Products</td>
                        <td className="px-4 md:px-6 py-3 text-accent-700 font-medium">1-5 minutes</td>
                        <td className="px-4 md:px-6 py-3 text-foreground-600">20-60 minutes</td>
                      </tr>
                      <tr className="border-t border-background-200 bg-background-50/50">
                        <td className="px-4 md:px-6 py-3 text-foreground-700">Seller Verification</td>
                        <td className="px-4 md:px-6 py-3 text-accent-700 font-medium">Manually reviewed</td>
                        <td className="px-4 md:px-6 py-3 text-foreground-600">Self-researched</td>
                      </tr>
                      <tr className="border-t border-background-200">
                        <td className="px-4 md:px-6 py-3 text-foreground-700">Update Frequency</td>
                        <td className="px-4 md:px-6 py-3 text-accent-700 font-medium">Weekly</td>
                        <td className="px-4 md:px-6 py-3 text-foreground-600">Real-time (unfiltered)</td>
                      </tr>
                      <tr className="border-t border-background-200 bg-background-50/50">
                        <td className="px-4 md:px-6 py-3 text-foreground-700">Scam Risk</td>
                        <td className="px-4 md:px-6 py-3 text-accent-700 font-medium">Very low</td>
                        <td className="px-4 md:px-6 py-3 text-foreground-600">Moderate to high</td>
                      </tr>
                      <tr className="border-t border-background-200">
                        <td className="px-4 md:px-6 py-3 text-foreground-700">Category Organization</td>
                        <td className="px-4 md:px-6 py-3 text-accent-700 font-medium">Pre-organized</td>
                        <td className="px-4 md:px-6 py-3 text-foreground-600">Manual filtering</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <hr className="border-background-200" />

              {/* In-Content CTA */}
              <div className="rounded-2xl overflow-hidden bg-background-100/60">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20inviting%20scene%20of%20an%20organized%20desk%20with%20a%20laptop%20surrounded%20by%20small%20fashion%20items%20fabric%20samples%20and%20a%20notepad%20with%20handwritten%20notes%2C%20soft%20window%20light%20from%20behind%2C%20editorial%20photography%2C%20no%20text%2C%20creative%20studio%20aesthetic&width=1200&height=500&seq=article-cta-v2-ff&orientation=landscape"
                  alt="Explore AllChinaBuy Spreadsheet directory"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="p-6 md:p-8 text-center">
                  <p className="text-lg md:text-xl text-foreground-600 italic mb-4 font-light">
                    {t("article:ctaText", "Ready to start exploring? Head back to our homepage to browse the complete category directory.")}
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap"
                  >
                    {t("article:ctaButton", "Back to Home — Explore Spreadsheet")}
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200 text-center">
              <Link
                to="/"
                className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                ← Back to Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}